import { SwapHoriz } from "@mui/icons-material";
import { Box, Button, Typography, useTheme } from "@mui/material";
import type React from "react";
import { useState } from "react";
import { useSelector } from "react-redux";
import ResizableTextarea from "../../../components/ui/ResizableTextArea";
import { RootState } from "../../../store/Index";
import { tokens } from "../../../theme";
import TemplateSelectionModal from "../proposal-preview/components/TemplateSectionModal";

interface IntroductionLetterSectionProps {
  value: string;
  onChange: (value: string) => void;
  bookingType: "FUNERAL" | "EVENT" | "CONFERENCE";
  proposalData?: {
    clientName?: string;
    deceasedName?: string;
    eventTitle?: string;
    deceasedBirthDate?: string;
    deceasedPassedDate?: string;
    serviceVenue?: string;
    serviceDate?: string;
  };
}

const IntroductionLetterSection: React.FC<IntroductionLetterSectionProps> = ({
  value,
  onChange,
  bookingType,
  proposalData,
}) => {
  const theme = useTheme();
  const reduxTheme = useSelector((state: RootState) => state.theme);
  const colors = tokens(theme.palette.mode, reduxTheme);

  const [dialogOpen, setDialogOpen] = useState(false);

  const replacePlaceholders = (template: string): string => {
    if (!proposalData) return template;

    let result = template;

    const replacements: Record<string, string> = {
      "{client_name}": proposalData.clientName || "",
      "{deceased_name}": proposalData.deceasedName || "",
      "{event_title}": proposalData.eventTitle || "",
      "{date_of_birth}": proposalData.deceasedBirthDate || "",
      "{date_of_death}": proposalData.deceasedPassedDate || "",
      "{service_venue}": proposalData.serviceVenue || "",
      "{service_date}": proposalData.serviceDate || "",
      "{current_date}": new Date().toLocaleDateString("en-US", {
        year: "numeric",
        month: "long",
        day: "numeric",
      }),
    };

    Object.entries(replacements).forEach(([placeholder, value]) => {
      if (value) {
        result = result.replace(
          new RegExp(placeholder.replace(/[{}]/g, "\\$&"), "g"),
          value,
        );
      }
    });

    return result;
  };

  const handleApplyTemplate = (templateBody: string) => {
    const replacedTemplate = replacePlaceholders(templateBody);
    onChange(replacedTemplate);
  };

  return (
    <Box>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 2,
        }}
      >
        <Typography variant="h6" sx={{ fontWeight: 600 }}>
          Introduction Letter
        </Typography>
        <Button
          startIcon={<SwapHoriz />}
          variant="outlined"
          size="small"
          onClick={() => setDialogOpen(true)}
          sx={{
            mr: 1,
            borderColor: "#555",
            border: 1,
            "&:hover": {
              borderColor: colors.primary[500],
              backgroundColor: `${colors.primary[400]}10`,
            },
          }}
        >
          Choose Template
        </Button>
      </Box>

      <ResizableTextarea
        label="Letter"
        value={value}
        onChange={onChange}
        colors={colors}
        reduxTheme={reduxTheme}
        placeholder="Enter your introduction letter or click 'Choose Template'..."
      />

      <TemplateSelectionModal
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        onApply={handleApplyTemplate}
        bookingType={bookingType}
        proposalData={proposalData}
      />
    </Box>
  );
};

export default IntroductionLetterSection;
